import { PageLayout } from '@components/PageLayout/PageLayout';
import { NextPage } from 'next';
import s from '../postPage.module.scss';
import { ExpectedProps, ServerPropsFactory } from '@lib/factory/serverprops';
import { TextEditor } from '@components/UIKit/TextToolbar';
import { PostManager } from '@data/post/PostManager';
import { isPostPersonal, PostEventState } from '@services/posts/helpers';
import { LinksEnum } from '@specs/navigation';
import { Breadcrumb } from '@components/UIKit/Breadcrumb/Breadcrumb';
import { PostView } from '@data/post/PostManagerSpec';
import { useUploadPost } from '@services/posts/hooks/useUploadPost';
import { useCallback, useEffect, useState } from 'react';
import { Info } from '@components/Pages/PostPage/Info/Info';
import cn from 'classnames';
import { useShowToast } from '@hooks/useShowToast';
import { api } from '@api';
import { useRouter } from 'next/router';
import { post } from '@prisma/client';
export const getServerSideProps = ServerPropsFactory(async (props: ExpectedProps) => {
  const id = props.query?.id?.[0];
  const post = id ? await PostManager.getPost(Number(id)) : await Promise.resolve(null);
  const personalPost = !id ? true : isPostPersonal(props.session?.uid, post?.author_id);
  const access = !id ? true : personalPost;
  return {
    ...props,
    post_id: id,
    access,
    post
  } satisfies PageProps;
}, [{
  key: 'access',
  destination: LinksEnum.FEED_POSTS
}]);
interface PageProps extends ExpectedProps {
  post_id: string | undefined;
  post: PostView | null;
  access: boolean;
}
const Page: NextPage<PageProps> = ({
  post
}) => {
  const {
    view,
    editor
  } = TextEditor({
    content: post?.content_new ?? [],
    isEditable: true
  });
  const content_new = editor?.getJSON().content;
  const {
    showToast
  } = useShowToast();
  const utils = api.useUtils();
  const router = useRouter();
  const [buttonState, setButtonState] = useState<PostEventState>('disabled');
  const {
    uploadPost,
    updateUploadPost,
    error
  } = useUploadPost();
  const effectAfterUpload = async (id: post['id']) => {
    {
      await utils.PostsManager.getAuthorFeed.invalidate();
      await utils.PostsManager.getPersonalFeed.invalidate();
      await utils.PostsManager.getPost.invalidate({
        post_id: id
      });
      showToast({
        variant: 'success'
      });
      router.push({
        pathname: LinksEnum.POST,
        query: {
          slug: id
        }
      });
    }
  };
  const submitListener = useCallback(() => {
    if (!content_new) return;
    const base_data = {
      content_new,
      type: 'PERSONAL' as const
    };
    if (post) {
      return updateUploadPost({
        ...base_data,
        author_id: post.author_id,
        id: post.id
      }, {
        onSuccess: async (_, variables) => {
          await effectAfterUpload(variables.id);
        }
      });
    }
    return uploadPost({
      ...base_data,
      published: true
    }, {
      onSuccess: async data => {
        await effectAfterUpload(data.id);
      }
    });
  }, [content_new, post]);
  useEffect(() => {
    document.addEventListener('post_submit', submitListener as EventListener);
    return () => {
      document.removeEventListener('post_submit', submitListener as EventListener);
    };
  }, [content_new]);
  useEffect(() => {
    const submitListener = (e: CustomEvent<PostEventState>) => {
      setButtonState(e.detail);
    };
    document.addEventListener('post_edit', submitListener as EventListener);
    return () => {
      document.removeEventListener('post_edit', submitListener as EventListener);
    };
  }, []);
  return <PageLayout data-sentry-element="PageLayout" data-sentry-component="Page" data-sentry-source-file="[[...id]].tsx">
            <div className={s.postPage}>
                <div className={'_container'}>
                    <Breadcrumb data-sentry-element="Breadcrumb" data-sentry-source-file="[[...id]].tsx" />

                    <div className={cn(s.postPage__box, s.postPage__box_create)}>
                        {view}

                        <Info valid={!error && buttonState !== 'error'} data-sentry-element="Info" data-sentry-source-file="[[...id]].tsx" />
                    </div>

                </div>
            </div>
        </PageLayout>;
};
export default Page;